<template>
  <div class="card p-1">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Payroll Approval"
      />
    </div>

    <div class="row px-1 mt-1">
      <div class="col-12 col-sm-6 col-md-3 col-lg-3 mb-1">
        <div class="mb-1">
          <label for="colFormLabel" class="col-form-label">Payroll Category</label>
          <v-select
              placeholder="Select Payroll Category"
              v-model="payrollCategory"
              :options="payrollCategoryList"
              :reduce="name => name.value"
              label="name"
          />
        </div>
      </div>
      <div class="col-12 col-sm-6 col-md-3 col-lg-2 mb-1">
        <div class="mb-1">
          <label class="col-form-label d-none d-sm-block">&nbsp;</label>
          <button
              style="min-width: 64px;"
              @click="onClickToSearch"
              class="btn btn-primary btn-block waves-effect waves-float waves-light"
          > GO
          </button>
        </div>
      </div>
    </div>

    <div class="col-12 mt-2 mb-4" style="min-height: 400px">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
          <tr>
            <th class="text-center">Payroll Category</th>
            <th class="text-center">Calculation Period</th>
            <th class="text-center">Start Date</th>
            <th class="text-center">End Date</th>
            <th class="text-center">Total Earning</th>
            <th class="text-center">Total Deduction</th>
            <th class="text-center">Total Net Payable</th>
            <th class="text-center">Status</th>
            <th class="text-center">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, i) in payrollApprovalList" :key="i">
            <td class="text-center text-capitalize">{{ item.payroll_category_name }}</td>
            <td class="text-center">{{ item.payroll_period_name }}</td>
            <td class="text-center">{{ item.payroll_start_date }}</td>
            <td class="text-center">{{ item.payroll_end_date }}</td>
            <td class="text-center">{{ item.total_earning }}</td>
            <td class="text-center">{{ item.total_deduction }}</td>
            <td class="text-center">{{ item.total_net_payable }}</td>
            <td class="text-center text-capitalize">{{ item.payroll_approval_status_name }}</td>
            <td class="text-center">
              <button class="btn btn-primary btn-sm mx-1 mb-1"
                      @click="goToShow(item, 'payroll-view')"
                      style="cursor: pointer"
                      title="View"
              >
                <i class="fas fa-eye"></i>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
        <p v-if="payrollApprovalList.length < 1" class="text-center mt-5">No data</p>
      </div>
    </div>

    <div class="px-2 position-absolute bottom-0 mt-4">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton'
import Pagination from '@/components/atom/Pagination'
import Loader from '@/components/atom/LoaderComponent'
import {onMounted, ref} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'
import handleHrPayroll from '@/services/modules/hrPayroll/hrPayroll'

const store = useStore()
const route = useRoute()
const router = useRouter()
const loading = ref(false)
const params = ref({
  offset: 20,
})
const payrollApprovalList = ref([])
const payrollCategory = ref('')
const payrollCategoryList = ref([])
const allowedCategories = ['overtime', 'absent_deduction']

const {
  fetchTypes,
  fetchPayrollProcessData
} = handleHrPayroll()

const getTypes = async () => {
  let query = `?company_id=${route.params.companyId}`
  await fetchTypes(query).then(res => {
    if (!res.status) {
      payrollCategoryList.value = []
      return
    }
    const categories = res.data.payrollCategory || []
    payrollCategoryList.value = categories.filter(item => allowedCategories.includes(item.value))
  })
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
}

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getPayrollApprovalList()
}

const goToShow = (item, routeName = 'payroll-view') => {
  let param = {
    companyId: route.params.companyId,
    moduleId: route.params.moduleId,
    menuId: route.params.menuId,
    pageId: route.params.pageId,
    payrollMasterId: item.id
  }

  let query = Object.assign({}, route.query, {from: 'approval'})

  router.push({
    name: routeName,
    params: param,
    query: query
  })
}

const onClickToSearch = () => {
  getPayrollApprovalList()
}

const getPayrollApprovalList = async () => {
  const category = payrollCategory.value || allowedCategories.join(',')

  let query = `?company_id=${route.params.companyId}`
  if (route.query.page) query += '&page=' + route.query.page
  query += '&offset=' + params.value.offset
  query += '&approval_status=draft'
  query += '&payroll_category=' + category

  loading.value = true
  payrollApprovalList.value = []

  await fetchPayrollProcessData(query).then(res => {
    if (!res.status) {
      store.commit('resetPagination')
      return
    }
    payrollApprovalList.value = res.data.data
    setPagination(res.data)
  }).finally(() => {
    loading.value = false
  })
}

onMounted(() => {
  Promise.all([
    getTypes(),
    getPayrollApprovalList()
  ])
})
</script>
